import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type SendbirdChatType from "@sendbird/chat";
import type { SendbirdChatParams } from "@sendbird/chat";
import type { GroupChannelModule as GroupChannelModuleType } from "@sendbird/chat/groupChannel";
import type { OpenChannelModule as OpenChannelModuleType } from "@sendbird/chat/openChannel";

import { useAppSelector } from "app/hooks";
import notificationSound from "assets/audio/chat-notification-sound.mp3";
import userPhotoFallback from "assets/images/user.png";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";
import { useGetMySendBirdDetailsQuery } from "services/users";
import { viteEnv } from "utils/vite-env.util";

interface AppWithGlobalChatProps {
  children: React.ReactNode;
}

const GlobalChatContext = createContext({
  unreadCount: 0,
  lastMessage: null,
  hasNewMessages: false,
  refreshUnreadCount: () => {},
  clearNotifications: () => {},
  sdk: null as any,
  isSDKReady: false
});

export const useGlobalChat = () => useContext(GlobalChatContext);

function GlobalMessageEventProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const [customSDK, setCustomSDK] = useState<SendbirdChatType | null>(null);
  const [isSDKReady, setIsSDKReady] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isOnChatPage = location.pathname.includes("/chat");
  const isNotificationSupported = typeof Notification !== "undefined";

  const [unreadCount, setUnreadCount] = useState(0);
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  const { data: sendbirdDetails } = useGetMySendBirdDetailsQuery(undefined, {
    skip: !currentUser?.sendbird_user_id
  });

  // Initialize custom SDK
  useEffect(() => {
    const initializeSDK = async () => {
      if (
        !currentUser?.sendbird_user_id ||
        !sendbirdDetails?.access_token ||
        customSDK
      ) {
        return;
      }

      try {
        const [
          { default: SendbirdChat },
          { GroupChannelModule },
          { OpenChannelModule }
        ] = await Promise.all([
          import("@sendbird/chat"),
          import("@sendbird/chat/groupChannel"),
          import("@sendbird/chat/openChannel")
        ]);
        const params: SendbirdChatParams<
          [GroupChannelModuleType, OpenChannelModuleType]
        > = {
          appId: viteEnv.VITE_SENDBIRD_APP_ID,
          modules: [new GroupChannelModule(), new OpenChannelModule()],
          localCacheEnabled: true
        };
        const sdk = await SendbirdChat.init(params);
        await sdk.connect(
          currentUser.sendbird_user_id,
          sendbirdDetails.access_token
        );

        setCustomSDK(sdk);
        setIsSDKReady(true);
      } catch (error) {
        console.error("Failed to initialize custom SDK:", error);
        setIsSDKReady(false);
      }
    };

    initializeSDK();
  }, [currentUser?.sendbird_user_id, sendbirdDetails?.access_token, customSDK]);

  const updateGlobalUnreadCount = useCallback(() => {
    if (!customSDK || !customSDK.groupChannel) return;
    customSDK.groupChannel
      .getTotalUnreadMessageCount()
      .then((count: number) => setUnreadCount(count))
      .catch((_e: any) => {});
  }, [customSDK]);

  const showGlobalNotification = useCallback(
    (channel: any, message: any) => {
      if (!isNotificationSupported || Notification.permission !== "granted") {
        return;
      }
      if (Notification.permission === "granted") {
        const isTabActive = !document.hidden;

        if (!isOnChatPage || !isTabActive) {
          const notification = new Notification(
            `💬 New message in ${channel.name || "Chat"}`,
            {
              body: message.message || "File attachment",
              icon: message.sender?.profileUrl || userPhotoFallback,
              tag: `global-msg-${channel.url}`,
              data: {
                channelUrl: channel.url,
                messageId: message.messageId,
                practiceId: currentPractice?.id,
                type: "message"
              }
            }
          );

          notification.onclick = () => {
            window.focus();
            // Navigate to chat page
            navigate("/chat");
            notification.close();
          };

          setTimeout(() => notification.close(), 7000);
        }
      }
    },
    [currentPractice?.id, isNotificationSupported, isOnChatPage, navigate]
  );

  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio(notificationSound);
      audio.volume = 0.3;
      audio.play().catch((e) => console.log("Could not play sound:", e));
    } catch (error) {
      console.log("Audio not available:", error);
    }
  }, []);

  const showGlobalMention = useCallback(
    (channel: any, message: any) => {
      if (!isNotificationSupported || Notification.permission !== "granted") {
        return;
      }
      if (Notification.permission === "granted") {
        const notification = new Notification(
          `🔔 You were mentioned in ${channel.name || "Chat"}`,
          {
            body: message.message,
            icon: message.sender?.profileUrl || "/mention-icon.png",
            tag: `global-mention-${channel.url}-${message.messageId}`,
            requireInteraction: true, // Stays until user clicks
            data: {
              channelUrl: channel.url,
              messageId: message.messageId,
              type: "mention"
            }
          }
        );

        notification.onclick = () => {
          window.focus();
          navigate("/chat");
          notification.close();
        };
      }
      playNotificationSound();
    },
    [isNotificationSupported, navigate, playNotificationSound]
  );

  const handleGlobalNewMessage = useCallback(
    (channel: any, message: any) => {
      // Update global state
      updateGlobalUnreadCount();
      setLastMessage({
        id: message.messageId,
        text: message.message || "File attachment",
        sender: message.sender?.nickname || "Unknown",
        channelName: channel.name || "Unknown Channel",
        channelUrl: channel.url,
        timestamp: message.createdAt
      });
      setHasNewMessages(true);

      showGlobalNotification(channel, message);

      // Play sound notification if enabled by user - https://linear.app/suno/issue/SN-13182/play-sound-ping-when-new-chat-message-comes
      if (currentUser?.chat_sound_notification) {
        playNotificationSound();
      }
    },
    [
      showGlobalNotification,
      updateGlobalUnreadCount,
      currentUser?.chat_sound_notification,
      playNotificationSound
    ]
  );

  const handleGlobalMention = useCallback(
    (channel: any, message: any) => {
      // Show high-priority notification for mentions
      showGlobalMention(channel, message);

      // Update state with mention
      setLastMessage({
        id: message.messageId,
        text: message.message,
        sender: message.sender?.nickname || "Unknown",
        channelName: channel.name || "Unknown Channel",
        channelUrl: channel.url,
        timestamp: message.createdAt,
        isMention: true
      });
      setHasNewMessages(true);
    },
    [showGlobalMention]
  );

  const setupGlobalHandlers = useCallback(async () => {
    if (!customSDK) return;

    const [{ default: GroupChannelHandler }, { default: ConnectionHandler }] =
      await Promise.all([
        import("@sendbird/uikit-react/handlers/GroupChannelHandler"),
        import("@sendbird/uikit-react/handlers/ConnectionHandler")
      ]);

    const channelHandler = new GroupChannelHandler({
      onMessageReceived: (channel: any, message: any) => {
        if (message.sender?.userId !== currentUser.sendbird_user_id) {
          handleGlobalNewMessage(channel, message);
        }
      },
      onMentionReceived: (channel: any, message: any) => {
        handleGlobalMention(channel, message);
      },
      onChannelChanged: (_channel: any) => {
        updateGlobalUnreadCount();
      },
      onUnreadMemberStatusUpdated: (_channel: any) => {
        updateGlobalUnreadCount();
      }
    });

    const connectionHandler = new ConnectionHandler({
      onReconnectSucceeded: () => {
        updateGlobalUnreadCount();
      },
      onDisconnected: () => {
        setIsSDKReady(false);
      },
      onReconnectStarted: () => {
        // Reconnection attempt in progress
      },
      onReconnectFailed: () => {
        setIsSDKReady(false);
        console.warn("Sendbird reconnection failed");
      }
    });

    const handlerId = `global-chat-handler-${currentPractice?.id}`;
    const connectionHandlerId = `global-connection-handler-${currentPractice?.id}`;

    customSDK.groupChannel.addGroupChannelHandler(handlerId, channelHandler);
    customSDK.addConnectionHandler(connectionHandlerId, connectionHandler);
    updateGlobalUnreadCount();
  }, [
    customSDK,
    currentPractice?.id,
    updateGlobalUnreadCount,
    currentUser.sendbird_user_id,
    handleGlobalNewMessage,
    handleGlobalMention
  ]);

  const cleanupGlobalHandlers = useCallback(() => {
    if (!customSDK) return;
    const handlerId = `global-chat-handler-${currentPractice?.id}`;
    const connectionHandlerId = `global-connection-handler-${currentPractice?.id}`;
    if (customSDK.groupChannel?.removeGroupChannelHandler) {
      customSDK.groupChannel.removeGroupChannelHandler(handlerId);
    }
    if (customSDK.removeConnectionHandler) {
      customSDK.removeConnectionHandler(connectionHandlerId);
    }
  }, [customSDK, currentPractice?.id]);

  useEffect(() => {
    if (
      !customSDK ||
      !currentUser.sendbird_user_id ||
      !currentPractice?.sendbird_chat_enabled
    ) {
      return;
    }

    if (customSDK.connectionState === "OPEN") {
      setupGlobalHandlers().catch(() => {});
    } else if (customSDK.connectionState === "CLOSED") {
      if (currentUser.sendbird_user_id && sendbirdDetails?.access_token) {
        customSDK
          .connect(currentUser.sendbird_user_id, sendbirdDetails.access_token)
          .then(() => {
            setupGlobalHandlers().catch(() => {});
          })
          .catch((_e: any) => {});
      }
    }

    return () => {
      cleanupGlobalHandlers();
    };
  }, [
    customSDK,
    currentUser.sendbird_user_id,
    currentPractice?.sendbird_chat_enabled,
    setupGlobalHandlers,
    cleanupGlobalHandlers,
    sendbirdDetails?.access_token
  ]);

  const clearNotifications = useCallback(() => {
    setUnreadCount(0);
    setHasNewMessages(false);
    setLastMessage(null);
  }, []);

  // Context value
  const contextValue = {
    unreadCount,
    lastMessage,
    hasNewMessages,
    refreshUnreadCount: updateGlobalUnreadCount,
    clearNotifications,
    sdk: customSDK,
    isSDKReady
  };

  return (
    <GlobalChatContext.Provider value={contextValue}>
      {children}
    </GlobalChatContext.Provider>
  );
}

export function AppWithGlobalChat({
  children
}: AppWithGlobalChatProps): JSX.Element {
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const isNotificationSupported = typeof Notification !== "undefined";
  const { data: sendbirdDetails } = useGetMySendBirdDetailsQuery(undefined, {
    skip: !currentUser?.sendbird_user_id
  });

  useEffect(() => {
    if (!isNotificationSupported) {
      return;
    }
    if (
      currentPractice?.sendbird_chat_enabled &&
      currentUser.sendbird_user_id &&
      sendbirdDetails?.access_token &&
      Notification.permission === "default"
    ) {
      Notification.requestPermission().then((_permission) => {});
    }
  }, [
    currentPractice?.sendbird_chat_enabled,
    currentUser.sendbird_user_id,
    isNotificationSupported,
    sendbirdDetails?.access_token
  ]);

  return <GlobalMessageEventProvider>{children}</GlobalMessageEventProvider>;
}
